/**
 * Deck list parser — frontend only (ESM).
 *
 * CSV column order: quantity, name, set_code, condition, finish, is_proxy, notes
 * Only quantity and name are required; all other columns are optional.
 * Unknown condition or finish values fall back to defaults (NM / nonfoil).
 */

const VALID_CONDITIONS = ['MINT', 'NM', 'LP', 'MP', 'HP', 'DAMAGED'];
const VALID_FINISHES = ['nonfoil', 'foil', 'etched'];

const DEFAULT_CONDITION = 'NM';
const DEFAULT_FINISH = 'nonfoil';
const DEFAULT_IS_PROXY = false;

function makeEntry(overrides) {
  return {
    name: '',
    quantity: 1,
    setCode: null,
    condition: DEFAULT_CONDITION,
    finish: DEFAULT_FINISH,
    isProxy: DEFAULT_IS_PROXY,
    isCommander: false,
    isSideboard: false,
    notes: null,
    ...overrides,
  };
}

// Regex patterns for format detection
const ARENA_COLLECTOR_RE = /\([A-Za-z0-9]{2,6}\)\s+\d+/m;
const MOXFIELD_SET_RE = /\[[A-Za-z0-9]{2,6}\]/m;

/**
 * Infers format from content heuristics:
 *   - CSV:   first non-empty line contains a comma
 *   - Arena: contains "(SET) collector-number" or "(SET)" annotation
 *   - Moxfield/Archidekt: contains "[SET]" annotation
 *   - else:  plain text / MTGO
 */
function detectFormat(text) {
  const firstNonEmpty = text.split('\n').find((l) => l.trim() !== '');
  if (firstNonEmpty && firstNonEmpty.includes(',')) return 'csv';
  if (ARENA_COLLECTOR_RE.test(text) || /\([A-Za-z0-9]{2,6}\)\s*$/m.test(text)) return 'arena';
  if (MOXFIELD_SET_RE.test(text)) return 'plaintext'; // Moxfield uses plaintext parser with [SET] support
  return 'plaintext';
}

/**
 * Parses plain-text / MTGO / Moxfield / Archidekt format.
 *
 * Line rules:
 *   - Lines starting with // or # are comments → skip (unless they're section headers)
 *   - Section headers: Deck, Main, Mainboard, Commander, Sideboard, Companion (with or without // prefix)
 *   - Card lines: "<qty> <name>" or "<qty>x <name>"
 *   - Set codes: (SET) or [SET] — optional, stripped from name
 *   - Finish markers: *F* = foil, *E* = etched — optional
 *   - All other lines are silently skipped
 */
function parsePlaintext(text) {
  const entries = [];
  let section = 'deck'; // deck, commander, sideboard
  let seenHeader = false;

  for (const line of text.split('\n')) {
    const trimmed = line.trim();

    // A blank line after at least one mainboard card ends the mainboard — everything
    // after is treated as sideboard (standard MTGO paste format).
    if (!trimmed) {
      if (entries.length > 0 && section === 'deck' && !seenHeader) {
        section = 'sideboard';
      }
      continue;
    }

    // Recognize section headers for Archidekt, Moxfield, MTGO formats
    // Also handle comment-style headers like "// COMMANDER" or "// MAINDECK"
    const headerMatch = trimmed.match(/^(?:\/\/\s*)?(?:Deck|Main(?:board|deck)?|Commander|Sideboard|Companion)\b/i);
    if (headerMatch) {
      seenHeader = true;
      if (/(?:Deck|Main(?:board|deck)?)\b/i.test(trimmed)) {
        section = 'deck';
      } else if (/Commander\b/i.test(trimmed)) {
        section = 'commander';
      } else if (/Sideboard\b/i.test(trimmed)) {
        section = 'sideboard';
      } else if (/Companion\b/i.test(trimmed)) {
        section = 'deck';
      }
      continue;
    }

    if (trimmed.startsWith('//') || trimmed.startsWith('#')) continue;

    const match = trimmed.match(/^(\d+)x?\s+(.+)$/);
    if (!match) continue;

    const quantity = parseInt(match[1], 10);
    let rest = match[2].trim();
    let finish = DEFAULT_FINISH;
    let setCode = null;

    // Strip finish indicators: *F* = foil, *E* = etched
    rest = rest.replace(/\s*\*([FE])\*\s*/gi, (_, flag) => {
      if (finish === DEFAULT_FINISH) finish = flag.toUpperCase() === 'E' ? 'etched' : 'foil';
      return ' ';
    }).trim();

    // Extract set code from [SET] format (Moxfield/Archidekt) or (SET) format (Arena)
    // Moxfield/Archidekt: "Card Name [CMR]" or "Card Name [CMR] 268"
    // Arena: "Card Name (CMR) 268" or "Card Name (CMR)"
    const withBrackets = rest.match(/^(.*?)\s+\[([A-Za-z0-9]{2,6})\](?:\s+\d+)?$/);
    const withParens = rest.match(/^(.*?)\s+\(([A-Za-z0-9]{2,6})\)(?:\s+\d+)?$/);

    if (withBrackets) {
      rest = withBrackets[1].trim();
      setCode = withBrackets[2];
    } else if (withParens) {
      rest = withParens[1].trim();
      setCode = withParens[2];
    }

    // Strip trailing set-name annotation (e.g. "Sol Ring (Commander Masters)").
    // Set codes are ≤6 chars, so anything longer in trailing parens is a display name.
    rest = rest.replace(/\s+\([^)]{7,}\)\s*$/, '').trim();

    if (!rest) continue;

    entries.push(makeEntry({
      name: rest,
      quantity,
      setCode,
      finish,
      isCommander: section === 'commander',
      isSideboard: section === 'sideboard',
    }));
  }

  return entries;
}

/**
 * Parses MTG Arena export format.
 *
 * Section headers (bare lines): Deck, Commander, Sideboard, Companion
 * Card lines: "<qty> <name> (<SET>) <collector-number>"
 *   — set code and collector number are optional
 */
function parseArena(text) {
  const entries = [];
  // Default to 'deck' so that headerless Arena lists (detected via collector-number
  // pattern) still parse correctly instead of silently dropping all card lines.
  let section = 'deck';
  let sawHeader = false;
  let isSideboardByBlankLine = false;

  for (const line of text.split('\n')) {
    const trimmed = line.trim();

    // A blank line after at least one mainboard card (and before any explicit Sideboard
    // header) ends the mainboard — everything after is treated as sideboard.
    if (!trimmed) {
      if (entries.length > 0 && section === 'deck' && !sawHeader) isSideboardByBlankLine = true;
      if (isSideboardByBlankLine && section === 'deck') section = 'sideboard';
      continue;
    }

    // Section headers — use word-boundary so "Commander (1)" or "Sideboard (15)" still match.
    // "Main" and "Mainboard" are Archidekt aliases for the main deck section.
    if (/^(?:Deck|Main(?:board)?)\b/.test(trimmed)) { section = 'deck'; sawHeader = true; isSideboardByBlankLine = false; continue; }
    if (/^Commander\b/.test(trimmed)) { section = 'commander'; sawHeader = true; continue; }
    if (/^Sideboard\b/.test(trimmed)) { section = 'sideboard'; sawHeader = true; continue; }
    if (/^Companion\b/.test(trimmed)) { section = 'deck'; sawHeader = true; continue; }

    // If we haven't seen a header and the line doesn't look like a card (quantity name),
    // skip it — it may be a title or comment line at the top of the list.
    if (!sawHeader && !/^\d+x?\s+\S/.test(trimmed)) continue;

    // Parse quantity + the rest of the line ("1 " or "1x ")
    const qtyRest = trimmed.match(/^(\d+)x?\s+(.+)$/);
    if (!qtyRest) continue;

    const quantity = parseInt(qtyRest[1], 10);
    let rest = qtyRest[2];

    // Strip trailing finish indicator: "... 268 *F*" or "... *F*"
    let finish = DEFAULT_FINISH;
    rest = rest.replace(/\s*\*([FE])\*\s*$/i, (_, flag) => {
      finish = flag.toUpperCase() === 'E' ? 'etched' : 'foil';
      return '';
    }).trim();

    // Peel off "(SET) collector-number" or just "(SET)" from the end.
    // Set codes are 2–6 uppercase alphanumeric characters.
    let setCode = null;
    let name = rest;

    const withCollector = rest.match(/^(.*?)\s+\(([A-Za-z0-9]{2,6})\)\s+\S+$/);
    const setOnly = !withCollector && rest.match(/^(.*?)\s+\(([A-Za-z0-9]{2,6})\)$/);

    if (withCollector) {
      name = withCollector[1];
      setCode = withCollector[2];
    } else if (setOnly) {
      name = setOnly[1];
      setCode = setOnly[2];
    }

    // Strip inline finish indicator embedded in name (e.g. "Sol Ring *F* (CMR) 268")
    name = name.replace(/\s*\*([FE])\*\s*/gi, (_, flag) => {
      if (finish === DEFAULT_FINISH) finish = flag.toUpperCase() === 'E' ? 'etched' : 'foil';
      return ' ';
    }).trim();

    // Strip trailing set-name annotation from name (e.g. "Sol Ring (Commander Masters)").
    // Set codes are ≤6 chars, so anything longer in trailing parens is a display name, not a code.
    name = name.replace(/\s+\([^)]{7,}\)\s*$/, '').trim();

    if (!name) continue;

    entries.push(makeEntry({
      name,
      quantity,
      setCode,
      finish,
      isCommander: section === 'commander',
      isSideboard: section === 'sideboard',
    }));
  }

  return entries;
}

/**
 * Splits a CSV row into columns, respecting double-quoted fields.
 * A quoted field may contain commas and escaped quotes ("").
 */
function splitCsvRow(line) {
  const cols = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      cols.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  cols.push(current.trim());
  return cols;
}

/**
 * Parses CSV format with fixed column order:
 *   quantity, name, set_code, condition, finish, is_proxy, notes
 *
 * First row is skipped if its first cell is non-numeric (header detection).
 * Only quantity and name are required.
 */
function parseCsv(text) {
  const entries = [];
  let firstDataLine = true;

  for (const line of text.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) continue;

    const cols = splitCsvRow(trimmed);

    if (firstDataLine) {
      firstDataLine = false;
      if (isNaN(parseInt(cols[0], 10))) continue; // skip header row
    }

    const quantity = parseInt(cols[0], 10);
    const name = cols[1] || '';
    if (isNaN(quantity) || !name) continue;

    const setCode = cols[2] || null;
    const rawCondition = (cols[3] || '').toUpperCase();
    const rawFinish = (cols[4] || '').toLowerCase();
    const rawIsProxy = (cols[5] || '').toLowerCase();
    const notes = cols[6] || null;

    const condition = VALID_CONDITIONS.includes(rawCondition) ? rawCondition : DEFAULT_CONDITION;
    const finish = VALID_FINISHES.includes(rawFinish) ? rawFinish : DEFAULT_FINISH;
    const isProxy = rawIsProxy === 'true' || rawIsProxy === '1';

    entries.push(makeEntry({
      name,
      quantity,
      setCode: setCode || null,
      condition,
      finish,
      isProxy,
      notes: notes || null,
    }));
  }

  return entries;
}

/**
 * Parses a deck list string into a structured array of card objects.
 *
 * @param {string} text - Raw deck list text
 * @param {'auto'|'plaintext'|'arena'|'mtgo'|'csv'|'moxfield'|'archidekt'} format - Parsing format; 'auto' detects from content
 * @returns {{ name, quantity, setCode, condition, finish, isProxy, isCommander, isSideboard, notes }[]}
 */
function parseDeckList(text, format = 'auto') {
  if (!text || typeof text !== 'string') return [];

  const resolvedFormat = format === 'auto' ? detectFormat(text) : format;

  switch (resolvedFormat) {
    case 'arena':
      return parseArena(text);
    case 'csv':
      return parseCsv(text);
    case 'plaintext':
    case 'mtgo':
    case 'moxfield':
    case 'archidekt':
    default:
      return parsePlaintext(text);
  }
}

export { parseDeckList };
